import { useState, useEffect, useCallback, useRef } from 'react'
import { BookOpen, RefreshCw, Loader2, FileText, Pencil, Save, X } from 'lucide-react'
import Panel from './Panel'
import DocsTree, { type TreeNode } from './DocsTree'
import { RichMarkdown } from '../content/RichMarkdown'
import { resolveApiBase } from '../../lib/mountBase'

interface DocsPanelProps {
  appName: string
  rootPath?: string
  isMaximized?: boolean
  onMaximize?: () => void
  onClose?: () => void
  closeable?: boolean
}

interface FileEntry {
  name: string
  path: string
  is_dir: boolean
  is_file: boolean
  size: number | null
  modified: number
}

interface FilesListResponse {
  path: string
  base_dir: string
  entries: FileEntry[]
}

interface FileContentResponse {
  path: string
  filename: string
  size: number
  content: string
}

/** Default .planning path inside the per-app workspace container. */
const DEFAULT_PLANNING_ROOT = 'main/.planning'

function planningRootForPath(rootPath?: string): string {
  if (!rootPath) return DEFAULT_PLANNING_ROOT
  const relativeRoot = rootPath
    .replace(/^\/home\/coder\/workspace\/?/, '')
    .replace(/\/+$/, '')
  return relativeRoot ? `${relativeRoot}/.planning` : DEFAULT_PLANNING_ROOT
}

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp']
function isImageFile(path: string): boolean {
  const lower = path.toLowerCase()
  return IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext))
}

/**
 * Parse STATE.md content to extract the current active phase number.
 * Matches "Phase: 34 of 39 (...)" pattern.
 */
function parseCurrentPhase(stateContent: string): number | null {
  const match = stateContent.match(/^Phase:\s*(\d+)\s+of\s+\d+/m)
  return match ? parseInt(match[1], 10) : null
}

/**
 * Convert file API entries into TreeNode array.
 */
function entriesToNodes(entries: FileEntry[]): TreeNode[] {
  return entries.map((entry) => ({
    name: entry.name,
    path: entry.path,
    is_dir: entry.is_dir,
    children: entry.is_dir ? [] : undefined,
    isLoaded: false,
  }))
}

/**
 * Recursively update a node in the tree by path, returning a new tree (immutable update).
 */
function updateNodeChildren(
  nodes: TreeNode[],
  targetPath: string,
  children: TreeNode[]
): TreeNode[] {
  return nodes.map((node) => {
    if (node.path === targetPath) {
      return { ...node, children, isLoaded: true }
    }
    if (node.is_dir && node.children && node.children.length > 0) {
      return { ...node, children: updateNodeChildren(node.children, targetPath, children) }
    }
    return node
  })
}

export default function DocsPanel({
  appName,
  rootPath,
  isMaximized,
  onMaximize,
  onClose,
  closeable,
}: DocsPanelProps) {
  const apiBase = resolveApiBase(typeof window !== 'undefined' ? window.location.pathname : '/')
  const planningRoot = planningRootForPath(rootPath)
  const [tree, setTree] = useState<TreeNode[]>([])
  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [content, setContent] = useState<string | null>(null)
  const [contentLoading, setContentLoading] = useState(false)
  const [contentError, setContentError] = useState<string | null>(null)
  const [activePhase, setActivePhase] = useState<number | null>(null)
  const [hasPlanning, setHasPlanning] = useState<boolean | null>(null) // null = loading
  const [refreshing, setRefreshing] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [draftContent, setDraftContent] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const editorRef = useRef<HTMLTextAreaElement | null>(null)

  /**
   * Fetch a directory listing from the deployed-apps file API.
   */
  const loadDirectory = useCallback(
    async (path: string): Promise<FileEntry[] | null> => {
      const params = path ? `?path=${encodeURIComponent(path)}` : ''
      const response = await fetch(
        `${apiBase}/deployed-apps/${encodeURIComponent(appName)}/files${params}`
      )
      if (!response.ok) {
        if (response.status === 404) return null
        throw new Error('Failed to load directory')
      }
      const data: FilesListResponse = await response.json()
      return data.entries
    },
    [apiBase, appName]
  )

  /**
   * Fetch file content from the deployed-apps file API.
   */
  const loadFileContent = useCallback(
    async (path: string): Promise<string> => {
      const response = await fetch(
        `${apiBase}/deployed-apps/${encodeURIComponent(appName)}/files/content?path=${encodeURIComponent(path)}`
      )
      if (!response.ok) {
        throw new Error('Failed to load file content')
      }
      const data: FileContentResponse = await response.json()
      return data.content
    },
    [apiBase, appName]
  )

  /**
   * Initialize: load .planning/ root, parse STATE.md, auto-select default doc.
   */
  const initialize = useCallback(async () => {
    try {
      setTree([])
      setSelectedFile(null)
      setContent(null)
      setContentError(null)
      setActivePhase(null)
      setHasPlanning(null)
      setIsEditing(false)
      setDraftContent('')
      setSaveError(null)

      // 1. Load root .planning/ directory
      const entries = await loadDirectory(planningRoot)
      if (entries === null) {
        setHasPlanning(false)
        return
      }

      setHasPlanning(true)
      const nodes = entriesToNodes(entries)
      setTree(nodes)

      // 2. Try to load STATE.md and parse active phase
      const stateEntry = entries.find((e) => e.name === 'STATE.md')
      if (stateEntry) {
        try {
          const stateContent = await loadFileContent(stateEntry.path)
          const phase = parseCurrentPhase(stateContent)
          setActivePhase(phase)
        } catch {
          // STATE.md may not be readable; continue without active phase
        }
      }

      // 3. Auto-select default document (ROADMAP.md preferred)
      const roadmap = entries.find((e) => e.name === 'ROADMAP.md')
      if (roadmap) {
        setSelectedFile(roadmap.path)
        try {
          setContentLoading(true)
          const roadmapContent = await loadFileContent(roadmap.path)
          setContent(roadmapContent)
        } catch {
          setContentError('Failed to load ROADMAP.md')
        } finally {
          setContentLoading(false)
        }
      } else {
        // Fall back to first .md file
        const firstMd = entries.find(
          (e) => !e.is_dir && e.name.endsWith('.md')
        )
        if (firstMd) {
          setSelectedFile(firstMd.path)
          try {
            setContentLoading(true)
            const mdContent = await loadFileContent(firstMd.path)
            setContent(mdContent)
          } catch {
            setContentError('Failed to load file')
          } finally {
            setContentLoading(false)
          }
        }
      }
    } catch {
      setHasPlanning(false)
    }
  }, [loadDirectory, loadFileContent, planningRoot])

  // Initialize on mount
  useEffect(() => {
    initialize()
  }, [initialize])

  useEffect(() => {
    if (isEditing) {
      editorRef.current?.focus()
    }
  }, [isEditing])

  const canEditSelectedFile = Boolean(selectedFile && content !== null && !isImageFile(selectedFile))
  const hasUnsavedChanges = isEditing && draftContent !== (content ?? '')

  function canLeaveCurrentFile(): boolean {
    if (saving) return false
    if (!hasUnsavedChanges) return true
    return window.confirm('Discard unsaved document changes?')
  }

  function exitEditMode() {
    setIsEditing(false)
    setDraftContent('')
    setSaveError(null)
    setSaving(false)
  }

  function startEditing() {
    if (!canEditSelectedFile || content === null) return
    setDraftContent(content)
    setSaveError(null)
    setIsEditing(true)
  }

  function cancelEditing() {
    if (!canLeaveCurrentFile()) return
    exitEditMode()
  }

  async function saveFileContent(path: string, nextContent: string): Promise<string> {
    const response = await fetch(
      `${apiBase}/deployed-apps/${encodeURIComponent(appName)}/files/content`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ path, content: nextContent }),
      }
    )
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to save file' }))
      const detail = typeof error?.detail === 'string' ? error.detail : 'Failed to save file'
      throw new Error(detail)
    }
    const data: FileContentResponse = await response.json()
    return data.content
  }

  async function handleSave() {
    if (!selectedFile || !isEditing) return
    setSaving(true)
    setSaveError(null)
    try {
      const savedContent = await saveFileContent(selectedFile, draftContent)
      setContent(savedContent)
      exitEditMode()
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save file')
    } finally {
      setSaving(false)
    }
  }

  /**
   * Handle file selection from the tree.
   */
  async function handleSelect(path: string) {
    if (!canLeaveCurrentFile()) return

    exitEditMode()
    setSelectedFile(path)
    setContentError(null)

    if (isImageFile(path)) {
      // For images, don't load as text — render directly as <img>
      setContent(null)
      setContentLoading(false)
      return
    }

    setContent(null)
    setContentLoading(true)
    try {
      const fileContent = await loadFileContent(path)
      setContent(fileContent)
    } catch {
      setContentError('Failed to load file')
    } finally {
      setContentLoading(false)
    }
  }

  /**
   * Handle lazy directory expansion from the tree.
   */
  async function handleExpand(path: string) {
    try {
      const entries = await loadDirectory(path)
      if (entries) {
        const children = entriesToNodes(entries)
        setTree((prev) => updateNodeChildren(prev, path, children))
      }
    } catch {
      // Failed to load directory children; silently ignore
    }
  }

  /**
   * Refresh: reload root, STATE.md, and current content.
   */
  async function handleRefresh() {
    if (!canLeaveCurrentFile()) return

    setRefreshing(true)
    exitEditMode()
    setTree([])
    setContent(null)
    setContentError(null)
    setActivePhase(null)
    setHasPlanning(null)
    setSelectedFile(null)
    await initialize()
    setRefreshing(false)
  }

  function handleEditorKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
      event.preventDefault()
      void handleSave()
    }
  }

  // -- Render --

  const toolbarActions = (
    <div className="flex items-center gap-1">
      {isEditing ? (
        <>
          <button
            onClick={cancelEditing}
            disabled={saving}
            className="p-1 rounded hover:bg-secondary transition-colors disabled:opacity-50"
            title="Cancel edit"
            aria-label="Cancel edit"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={saving || !hasUnsavedChanges}
            className="p-1 rounded hover:bg-secondary transition-colors disabled:opacity-50"
            title="Save document"
            aria-label="Save document"
          >
            <Save className={`w-3.5 h-3.5 ${saving ? 'animate-pulse' : ''}`} />
          </button>
        </>
      ) : (
        <button
          onClick={startEditing}
          disabled={!canEditSelectedFile}
          className="p-1 rounded hover:bg-secondary transition-colors disabled:opacity-50"
          title="Edit document"
          aria-label="Edit document"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        onClick={handleRefresh}
        disabled={refreshing || saving}
        className="p-1 rounded hover:bg-secondary transition-colors disabled:opacity-50"
        title="Refresh"
        aria-label="Refresh"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
      </button>
    </div>
  )

  // Empty state: .planning/ doesn't exist
  if (hasPlanning === false) {
    return (
      <Panel
        title="Docs"
        icon={<BookOpen className="w-4 h-4" />}
        isMaximized={isMaximized}
        onMaximize={onMaximize}
        onClose={onClose}
        closeable={closeable}
        extra={toolbarActions}
      >
        <div className="flex flex-col items-center justify-center h-full text-center p-6">
          <BookOpen className="w-10 h-10 text-muted-foreground mb-3 opacity-50" />
          <h3 className="text-sm font-medium mb-1">No project documents yet</h3>
          <p className="text-xs text-muted-foreground max-w-[250px]">
            Planning documents will appear here when the GSD framework generates them.
          </p>
        </div>
      </Panel>
    )
  }

  // Loading state
  if (hasPlanning === null) {
    return (
      <Panel
        title="Docs"
        icon={<BookOpen className="w-4 h-4" />}
        isMaximized={isMaximized}
        onMaximize={onMaximize}
        onClose={onClose}
        closeable={closeable}
      >
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      </Panel>
    )
  }

  return (
    <Panel
      title="Docs"
      icon={<BookOpen className="w-4 h-4" />}
      isMaximized={isMaximized}
      onMaximize={onMaximize}
      onClose={onClose}
      closeable={closeable}
      extra={toolbarActions}
    >
      <div className="flex h-full">
        {/* Left sidebar: tree navigation */}
        <div className="w-[240px] flex-shrink-0 border-r border-border overflow-y-auto">
          <DocsTree
            nodes={tree}
            selectedPath={selectedFile}
            activePhase={activePhase}
            onSelect={handleSelect}
            onExpand={handleExpand}
          />
        </div>

        {/* Right content area: markdown viewer */}
        <div className={`flex-1 min-w-0 ${isEditing ? 'flex flex-col' : 'overflow-y-auto'}`}>
          {isEditing ? (
            <>
              <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2 text-xs">
                <div className="min-w-0">
                  <div className="truncate font-medium text-foreground">
                    {selectedFile?.split('/').pop()}
                  </div>
                  <div className="truncate text-muted-foreground">{selectedFile}</div>
                </div>
                <div className="flex-shrink-0 text-muted-foreground">
                  {hasUnsavedChanges ? 'Unsaved changes' : 'No changes yet'}
                </div>
              </div>
              {saveError && (
                <div className="mx-4 mt-3 rounded border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {saveError}
                </div>
              )}
              <div className="flex-1 min-h-0 p-4">
                <textarea
                  ref={editorRef}
                  value={draftContent}
                  onChange={(event) => setDraftContent(event.target.value)}
                  onKeyDown={handleEditorKeyDown}
                  className="h-full w-full resize-none rounded-lg border border-border bg-background p-3 font-mono text-sm leading-6 outline-none focus:ring-2 focus:ring-primary/20"
                  spellCheck={false}
                  aria-label="Document editor"
                />
              </div>
            </>
          ) : contentLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : contentError ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <p className="text-sm text-destructive mb-2">{contentError}</p>
              <button
                onClick={() => selectedFile && handleSelect(selectedFile)}
                className="text-xs text-primary hover:underline"
              >
                Retry
              </button>
            </div>
          ) : selectedFile && isImageFile(selectedFile) ? (
            <div className="p-4 flex items-center justify-center">
              <img
                src={`${apiBase}/deployed-apps/${encodeURIComponent(appName)}/files/content?path=${encodeURIComponent(selectedFile)}&raw=true`}
                alt={selectedFile.split('/').pop() || ''}
                className="max-w-full max-h-[80vh] rounded shadow-sm"
                loading="lazy"
              />
            </div>
          ) : content !== null ? (
            <div className="p-4">
              <RichMarkdown
                content={content}
                appName={appName}
                basePath={selectedFile || undefined}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <FileText className="w-8 h-8 text-muted-foreground mb-2 opacity-50" />
              <p className="text-sm text-muted-foreground">
                Select a document from the tree
              </p>
            </div>
          )}
        </div>
      </div>
    </Panel>
  )
}
